// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#health-checks-and-crash-recovery
import * as React from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { api } from '@/lib/api';

const REFRESH_MS = 30_000;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * Crash-loop context the server attaches to `logs.tail`. The log viewer below
 * follows the stream and only reads `logs.tail` as its fallback, so the banner
 * keeps its own one-line read rather than depending on which transport is up.
 */
export function CrashLoopBanner({ appId }: { appId: string }): React.JSX.Element | null {
  const key = ['logs.crash-context', appId] as const;
  const { data } = api.logs.tail.useQuery(
    key,
    { params: { id: appId }, query: { limit: 1, since: '1m' } },
    { queryKey: key, refetchInterval: REFRESH_MS, refetchOnWindowFocus: false },
  );

  const crash = data?.status === 200 ? data.body.crash_context : undefined;
  if (!crash?.crash_looping) return null;

  const times = crash.restart_count === 1 ? 'once' : `${crash.restart_count} times`;
  return (
    <Alert variant="destructive" data-testid="crash-loop-banner">
      <AlertTitle>This app is crash-looping</AlertTitle>
      <AlertDescription>
        <p>
          Its container keeps exiting and has restarted {times}.
          {crash.reason && (
            <>
              {' '}
              Last exit reason: <span className="font-mono">{crash.reason}</span>.
            </>
          )}
          {crash.last_terminated_at && (
            <>
              {' '}
              Last exited{' '}
              <time dateTime={crash.last_terminated_at}>
                {formatWhen(crash.last_terminated_at)}
              </time>
              .
            </>
          )}{' '}
          The logs below show what it printed before exiting.
        </p>
      </AlertDescription>
    </Alert>
  );
}
